import { array } from 'fp-ts';
import {
    PaginatedRateGroupParameters,
    PolicyOverrideStatusEnumEnum,
    RateGroupOverview,
    RateGroupRate,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { ColumnFilter, ColumnFilters, EnumFilterType, Filter, StringFilterType, TableFilters } from '@optimus-web/core';
import { useSettlementRuleNameGetter } from '../../../components/common/SettlementRuleName';
import { useCancellationPolicyNameGetter } from '../../../components';

const convertNoBreakSpaceToSpace = (s: string) => s.replace(/\u00A0/g, ' ');

type ReadeGroupOverviewFilterFn = (row: RateGroupOverview) => boolean;

const getActiveRateNames = (rates: Array<RateGroupRate>) => rates.map(({ name }) => name.toLowerCase()).join(', ');

const isStringFilter = (filter: ColumnFilter): filter is Filter<StringFilterType, string> =>
    filter != null && Object.keys(StringFilterType).includes(filter.filterType);

const isMultiSelectFilterType = (filter: ColumnFilter): filter is Filter<EnumFilterType, string[]> =>
    filter != null && Object.keys(EnumFilterType).includes(filter.filterType);

const matchesStringFilter = (value: string | undefined | null, columnFilter: Filter<StringFilterType, string>) => {
    switch (columnFilter.filterType) {
        case StringFilterType.Contains:
            return !!value?.toLocaleLowerCase().includes(columnFilter.value.toLocaleLowerCase());
        case StringFilterType.Equals:
            return value?.toLocaleLowerCase() === columnFilter.value.toLocaleLowerCase();
    }
};

const useRateGroupOverviewFiltering = (tableFilters: TableFilters<RateGroupOverview>) => {
    const getSettlementRuleName = useSettlementRuleNameGetter();
    const getCancellationPolicyName = useCancellationPolicyNameGetter();

    const getFilterFn = ({
        columnName,
        columnFilter,
    }: {
        columnName: keyof RateGroupOverview;
        columnFilter: ColumnFilter;
    }): ReadeGroupOverviewFilterFn | undefined => {
        if (columnName === 'name' && isStringFilter(columnFilter)) {
            return (row) => matchesStringFilter(row.name, columnFilter);
        }

        if (columnName === 'settlementRules' && isStringFilter(columnFilter)) {
            return (row) =>
                !!row.settlementRules.find((pp) =>
                    matchesStringFilter(
                        convertNoBreakSpaceToSpace(getSettlementRuleName(pp, { format: 'nameAndParameters' })),
                        columnFilter
                    )
                );
        }

        if (columnName === 'cancellationPolicies' && isStringFilter(columnFilter)) {
            return (row) =>
                !!row.cancellationPolicies?.find((cp) =>
                    matchesStringFilter(
                        convertNoBreakSpaceToSpace(getCancellationPolicyName(cp, { format: 'nameAndParameters' })),
                        columnFilter
                    )
                );
        }

        if (columnName === 'activeRates' && isStringFilter(columnFilter)) {
            return (row) => !!matchesStringFilter(getActiveRateNames(row.activeRates), columnFilter);
        }

        if (columnName === 'policyOverrideStatus' && isMultiSelectFilterType(columnFilter)) {
            const filterValues = columnFilter.value.map(
                (f) => PolicyOverrideStatusEnumEnum[f as keyof typeof PolicyOverrideStatusEnumEnum]
            );

            return (row) =>
                row.policyOverrideStatus
                    ? filterValues.includes(row.policyOverrideStatus)
                    : filterValues.includes(PolicyOverrideStatusEnumEnum.Empty);
        }

        if (columnName === 'requirePaymentCardDuringOnlineCheckIn' && isMultiSelectFilterType(columnFilter)) {
            const filterValues = columnFilter.value;
            return (row) => {
                const rowValue = row.requirePaymentCardDuringOnlineCheckIn ? 'Required' : 'Hidden';
                return filterValues.includes(rowValue);
            };
        }

        return undefined;
    };

    const tableFilterFns: ReadeGroupOverviewFilterFn[] = (
        Object.entries<ColumnFilters>(tableFilters) as Array<[keyof RateGroupOverview, ColumnFilters]>
    )
        .flatMap(([columnName, columnFiters]) =>
            columnFiters ? columnFiters.map((columnFilter) => getFilterFn({ columnName, columnFilter })) : undefined
        )
        .filter((i) => !!i);

    return { tableFilterFns };
};

export const useRateGroupOverviewEntries = ({
    pagination,
    filters,
    ...rest
}: PaginatedRateGroupParameters & { filters: TableFilters<RateGroupOverview> }) => {
    const { tableFilterFns } = useRateGroupOverviewFiltering(filters);

    return useRepositoryQuery(
        'rateGroups',
        'getOverviewEntries',
        { ...rest },
        {
            staleTime: 5000,
            select: ({ data }) => {
                const filteredData = array.isEmpty(tableFilterFns)
                    ? data
                    : data.filter((row) => tableFilterFns.every((fn) => fn(row)));
                const paginatedData = pagination
                    ? filteredData.slice(
                          pagination.pageIndex * pagination.pageSize,
                          (pagination.pageIndex + 1) * pagination.pageSize
                      )
                    : filteredData;
                return {
                    totalRowCount: filteredData.length,
                    data: paginatedData,
                };
            },
        }
    );
};
